import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthSessionDto, LoginDto, PublicUserDto, SignupDto } from '@redpandev/shared';
import { api } from '../lib/api';
import { sessionGate } from '../lib/session-gate';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  user: PublicUserDto | null;
  status: AuthStatus;
  login: (dto: LoginDto) => Promise<void>;
  signup: (dto: SignupDto) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Adopte une session DÉJÀ émise par l'API (cookies posés) sans re-fetch : flux
   * social (onboarding `social/complete`, liaison `social/link/confirm`, spec 11)
   * où l'API répond un `AuthSessionDto`. Même effet que `login`/`signup`.
   */
  adoptSession: (session: AuthSessionDto) => void;
  /** Re-fetch de `me` (ex : après vérification d'email) pour rafraîchir badges & état. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUserDto | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  // Hydratation de session au montage (cookie httpOnly → GET /auth/me).
  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((u) => {
        sessionGate.noteAuthenticated(); // arme l'événement d'expiration (ADR 0016)
        if (alive) {
          setUser(u);
          setStatus('authenticated');
        }
      })
      .catch(() => {
        // Ne dégrade JAMAIS une session établie pendant la course : la chaîne
        // me→401→refresh peut se terminer APRÈS un login/signup réussi (autofill
        // rapide) — écraser `authenticated` ferait rebondir RequireAuth vers /login.
        // À `loading`, `user` est encore null : rien d'autre à nettoyer.
        if (alive) setStatus((s) => (s === 'loading' ? 'anonymous' : s));
      });
    return () => {
      alive = false;
    };
  }, []);

  // Session MORTE en cours d'usage (refresh KO — ADR 0016, spec 01 §5.4) :
  // transition EXPLICITE vers déconnecté. Chaque surface réagit à `status`
  // (RequireAuth → /login, Griffonne → bandeau « session expirée », header hub).
  useEffect(() => {
    return sessionGate.onExpired(() => {
      setUser(null);
      setStatus('anonymous');
    });
  }, []);

  // ⚠️ Mémoïsation manuelle NÉCESSAIRE : composant en bailout compiler (try sans
  // catch) — le React Compiler ne le couvre pas (ADR 0010, sonde 2026-07-05).
  const login = useCallback(async (dto: LoginDto) => {
    const session = await api.login(dto);
    // L'échéance du token arme le refresh PROACTIF (brique 3, ADR 0016 déc. 4).
    sessionGate.noteAuthenticated(session.accessExpiresIn);
    setUser(session.user);
    setStatus('authenticated');
  }, []);

  const signup = useCallback(async (dto: SignupDto) => {
    const session = await api.signup(dto);
    sessionGate.noteAuthenticated(session.accessExpiresIn);
    setUser(session.user);
    setStatus('authenticated');
  }, []);

  const adoptSession = useCallback((session: AuthSessionDto) => {
    sessionGate.noteAuthenticated(session.accessExpiresIn);
    setUser(session.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      sessionGate.noteLoggedOut(); // volontaire : pas d'événement « session expirée »
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  // Re-hydrate `me` à la demande (ex : emailVerified vient de changer côté serveur).
  // En cas d'échec transitoire on garde l'état courant : les gardes gèrent les 401 durables.
  const refreshUser = useCallback(async () => {
    try {
      const u = await api.me();
      sessionGate.noteAuthenticated();
      setUser(u);
      setStatus('authenticated');
    } catch {
      /* silencieux : pas de déconnexion forcée sur un simple re-fetch */
    }
  }, []);

  const value = useMemo(
    () => ({ user, status, login, signup, adoptSession, logout, refreshUser }),
    [user, status, login, signup, adoptSession, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>');
  return ctx;
}
